import React from 'react';
import { getCurrencySymbol } from '../utils/currency';
import { FaUpload, FaTrash, FaBirthdayCake, FaPaperPlane, FaEdit } from 'react-icons/fa';

const SUFFIX_TO_LABEL = {
  '10': '1', '15': '1.5', '20': '2', '25': '2.5', '30': '3',
  '35': '3.5', '40': '4', '45': '4.5', '50': '5', '55': '5.5', '60': '6',
};

const CustomProfileCard = ({
  theme,
  subtitle,
  flavour,
  cover,
  eggless,
  glutenFree,
  sponge,
  tiers = [],
  quantity,
  ccost,
  costEstimate,
  image,
  tierCount = 1,
  multiTier = false,
  moreTier = false,
  cakeChargeData = {},
  showPublishIcon = false,
  showOrderIcon = false,
  showDeleteIcon = false,
  showSendIcon = false,
  isFromHome = false,
  onPublish,
  onDelete,
  onOrder,
  onSend,
  onEdit,
  extraActions = null,
  badge = null,
}) => {
 
  // Format the description with additional details
  const formatDescription = () => {
    const tier1 = tiers?.[0];
    const resolvedFlavour = tier1?.cflavour || flavour || 'unknown';
    const resolvedFilling = tier1?.cfilling || (subtitle || '').replace(/^filling:\s*/i, '') || 'none';
    const resolvedSponge = tier1?.sponge || sponge || 'unknown';
    const flavourText = resolvedFlavour.toLowerCase();
    const capitalizedFlavour = flavourText.charAt(0).toUpperCase() + flavourText.slice(1);
    const subtitleText = resolvedFilling.toLowerCase();
    const coverText = (cover || 'none').toLowerCase();
    const spongeText = resolvedSponge.toLowerCase();

    const dietaryOptions = [];
    if (eggless) dietaryOptions.push('eggless');
    if (glutenFree) dietaryOptions.push('gluten free');
    const dietaryText = dietaryOptions.length > 0 ? `${dietaryOptions.join(' and ')} ` : '';

    const descriptionLine1 = `${capitalizedFlavour} flavour with ${subtitleText} filling, covered by ${coverText} on ${dietaryText}${spongeText} sponge.`;

    const rawQty = quantity && quantity !== 'null' ? String(quantity).replace(/\s*kg$/i, '').trim() : '';
    const baseSegment = moreTier ? '3T' : multiTier ? 'T' : 'N';
    const segNums = Object.keys(cakeChargeData)
      .filter(k => new RegExp(`^${baseSegment}\\d+$`).test(k))
      .map(k => parseInt(k.slice(baseSegment.length)))
      .filter(n => !isNaN(n));
    const baseQtyLabel = segNums.length ? (SUFFIX_TO_LABEL[String(Math.min(...segNums))] || null) : null;
    const quantityText = rawQty ? `${rawQty}kg` : (baseQtyLabel ? `${baseQtyLabel}kg` : '');
    const costValue = costEstimate && costEstimate !== 'null' && costEstimate !== null && costEstimate !== '' ? costEstimate : (ccost && ccost !== 'null' && ccost !== '' ? ccost : 'N/A');
    const formattedCostValue = costValue === 'N/A' ? 'N/A' : `${getCurrencySymbol()}${costValue.replace(/[^0-9.]/g, '')}`;

    return { descriptionLine1, quantityText, formattedCostValue };
  };

  const { descriptionLine1, quantityText, formattedCostValue } = formatDescription();

  // Define styles for the component, matching HomeProfileCard
  const styles = `
  .action-btn {
    color: #333 !important;
    background: rgba(255, 255, 255, 0.6) !important;
    border: none !important;
    border-radius: 6px;
    padding: 0 !important;
    margin: 0 !important;
    width: 28px !important;
    height: 28px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    flex-shrink: 0 !important;
    cursor: pointer;
  }

  .action-btn:disabled {
    cursor: not-allowed !important;
    opacity: 0.6;
  }

  html[data-theme="dark"] .action-btn {
    color: #ececec !important;
    background: rgba(0, 0, 0, 0.5) !important;
    border: none !important;
  }

  .profile-card {
    margin-left: 10px !important;
    margin-right: 10px !important;
    padding: 10px !important;
    box-sizing: border-box !important;
  }

  .profile-card h3 {
    font-size: clamp(12px, 2vw, 16px);
    line-height: 1.5em;
    margin-bottom: 5px !important;
  }

  .profile-card p {
    font-size: clamp(14px, 2vw, 18px);
    line-height: 1.3em;
  }

  .separator-line {
    width: 100%;
    border-top: 1px dotted #999;
    margin: 5px 0 2px 0;
  }

  .quantity-cost-wrapper {
    display: block;
  }

  .quantity-cost-item {
    white-space: normal;
    display: block;
  }

  @media (min-width: 601px) and (max-width: 900px) {
    .profile-card {
      margin-left: 8px !important;
      margin-right: 8px !important;
    }
    .profile-card h3 {
      font-size: clamp(14px, 1.5vw, 16px);
    }
    .profile-card p {
      font-size: clamp(14px, 1.5vw, 16px);
    }
  }

  @media (max-width: 600px) {
    .profile-card {
      margin-left: 5px !important;
      margin-right: 5px !important;
      aspect-ratio: 1 / 1.1 !important;
    }
    .profile-card h3 {
      font-size: clamp(10px, 1.5vw, 12px);
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .profile-card p {
      display: none !important;
    }
    .image-container {
      width: 100% !important;
      aspect-ratio: 1 / 1 !important;
    }
    .separator-line {
      display: none !important;
    }
  }
`;

  return (
    <div
      className="profile-card"
      onClick={onEdit}
      style={{
        minHeight: '150px',
      }}
    >
      <style>{styles}</style>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Image container with relative positioning */}
        <div
          className="image-container"
          style={{
            position: 'relative',
            width: '80%',
            alignSelf: 'center',
            aspectRatio: '4 / 3',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: 'var(--bg-section)',
          }}
        >
          {image ? (
            <img
              src={image}
              alt={theme || 'Profile'}
              className="card-image"
              style={{
                width: '100%',
                height: '100%',
                minWidth: '100%',
                minHeight: '100%',
                // The whole photo, scaled to fit the card (imports come in every shape).
                objectFit: 'contain',
                objectPosition: 'center',
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                fontSize: 'clamp(10px, 2vw, 14px)',
              }}
            >
              No Image
            </div>
          )}

          {badge && <span className="social-review-badge" title="Some fields were filled with defaults — please check">{badge}</span>}
          {/* Icons overlay */}
          {(showPublishIcon || showOrderIcon || showDeleteIcon || showSendIcon || extraActions) && (
            <div
              className="card-actions"
              style={{
                position: 'absolute',
                bottom: '0',
                left: '0',
                right: '0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                zIndex: 10,
                padding: '0',
                background: 'transparent',
              }}
            >
              <div
                className="card-actions-left"
                style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
              >
                {showPublishIcon && (
                  <button
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (isFromHome) {
                        onEdit();
                      } else {
                        onPublish();
                      }
                    }}
                    className="action-btn"
                    title={isFromHome ? "Edit Profile" : "Publish Profile"}
                    style={{ fontSize: '14px' }}
                  >
                    {isFromHome ? <FaEdit /> : <FaUpload />}
                  </button>
                )}
                {showOrderIcon && (
                  <button
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (isFromHome) {
                        onEdit();
                      } else {
                        onOrder();
                      }
                    }}
                    className="action-btn"
                    title={isFromHome ? "Edit Profile" : "Add to Cart"}
                    style={{ fontSize: '14px' }}
                  >
                    {isFromHome ? <FaEdit /> : <FaBirthdayCake />}
                  </button>
                )}
                {showSendIcon && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onSend(); }}
                    className="action-btn"
                    title="Send Enquiry"
                    style={{ fontSize: '14px' }}
                  >
                    <FaPaperPlane />
                  </button>
                )}
                {extraActions}
              </div>
              <div
                className="card-actions-right"
                style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
              >
                {showDeleteIcon && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="action-btn"
                    title="Delete Profile"
                    style={{ fontSize: '14px', color: '#fa4848 !important' }}
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content area */}
        <div style={{ display: 'flex', flexDirection: 'column', margin: '2px 5px 0 5px' }}>
          <h3
            style={{
              textAlign: 'center',
              margin: '4px 2px 1px 2px',
              color: 'var(--text-primary)',
            }}
          >
            {theme?.trim() ? theme : (tiers?.[0]?.cflavour || flavour)?.trim() || 'Untitled'}
          </h3>
          <p
            style={{
              color: 'var(--text-secondary)',
              margin: '4px 1px 0 1px',
              textAlign: 'justify',
              padding: '0 0 0 0',
              lineHeight: '1.2em',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 6,
            }}
          >
            {descriptionLine1}
          </p>
          <div className="separator-line" />
          <p
            className="quantity-cost-wrapper"
            style={{
              color: 'var(--text-secondary)',
              margin: '4px 1px 0 1px',
              textAlign: 'justify',
              padding: '0 0 0 0',
              lineHeight: '1.4em',
            }}
          >
            {!isFromHome && (
              <span className="quantity-cost-item">
                <span style={{ fontWeight: 'normal' }}>Quantity:</span> {quantityText}
              </span>
            )}
            <span className="quantity-cost-item">
              <span style={{ fontWeight: 'normal' }}>Cost Estimate:</span> {formattedCostValue}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CustomProfileCard);